import { Grid, Stack, Chip } from "@mui/material";
import type { AmlReviewStatistic } from "../model/index";
import { AmlStatus, StatusColor, StatusNaming } from "../constant/amlConstant";
import {
  IDVGroupMarkers,
  PEPGroupMarkers,
  RiskRatingGroupMarkers,
  SanctionsGroupMarkers,
} from "../constant/markerConstant";
import { SEARCH_STATUS_KEYS } from "./Filter";
import { isEmpty, isNil } from "lodash";

interface StatisticProps {
  value: AmlReviewStatistic;
  selected?: string;
  loading?: boolean;
  onSelect: (v: {
    [k in (typeof SEARCH_STATUS_KEYS)[number]]?: string;
  }) => void;
  onDeselect: () => void;
}

const DISABLED_COLOR = "#cacaca";

const Statistic: React.FC<StatisticProps> = ({
  value,
  selected,
  loading,
  ...props
}) => {
  const amlStatus = [
    AmlStatus.SUB,
    AmlStatus.REF,
    AmlStatus.EVI,
    AmlStatus.AUT,
    AmlStatus.NRQ,
    AmlStatus.ACT,
    AmlStatus.REJ,
  ];

  const isActive = (v: string) => {
    return isNil(selected) || isEmpty(selected) || selected === v;
  };

  const isSelected = (v: string) => {
    return selected === v;
  };

  const handleOnSelect = (
    v: string,
    payload: { [k in (typeof SEARCH_STATUS_KEYS)[number]]?: string }
  ) => {
    if (v === selected) {
      return;
    }
    props.onSelect(payload);
  };

  return (
    <Grid container sx={{ padding: "8px", background: "#fafafa" }} rowGap="6px">
      <Grid item xs={12}>
        <Stack
          direction="row"
          useFlexGap
          rowGap="6px"
          columnGap="8px"
          flexWrap="wrap"
        >
          {amlStatus.map((status) => {
            const color = isActive(status)
              ? StatusColor[status]
              : DISABLED_COLOR;
            const count = value[status];
            return (
              <Chip
                key={status}
                disabled={loading}
                size="small"
                label={`${count} - ${StatusNaming[status]}`}
                sx={{
                  backgroundColor: color,
                }}
                onDelete={
                  isSelected(status) ? () => props.onDeselect() : undefined
                }
                onClick={() => handleOnSelect(status, { status })}
              />
            );
          })}
        </Stack>
      </Grid>
      <Grid item xs={12}>
        <Stack
          direction="row"
          useFlexGap
          rowGap="6px"
          columnGap="8px"
          flexWrap="wrap"
        >
          <Chip
            size="small"
            disabled={loading}
            label={`${value[IDVGroupMarkers.REF]} - ID&V Referred`}
            sx={{
              backgroundColor: isActive(IDVGroupMarkers.REF)
                ? StatusColor[AmlStatus.REF]
                : DISABLED_COLOR,
            }}
            onDelete={
              isSelected(IDVGroupMarkers.REF)
                ? () => props.onDeselect()
                : undefined
            }
            onClick={() =>
              handleOnSelect(IDVGroupMarkers.REF, {
                idvStatus: IDVGroupMarkers.REF,
              })
            }
          />
          <Chip
            size="small"
            disabled={loading}
            label={`${value[SanctionsGroupMarkers.REF]} - Sanctions Referred`}
            sx={{
              backgroundColor: isActive(SanctionsGroupMarkers.REF)
                ? StatusColor[AmlStatus.REF]
                : DISABLED_COLOR,
            }}
            onDelete={
              isSelected(SanctionsGroupMarkers.REF)
                ? () => props.onDeselect()
                : undefined
            }
            onClick={() =>
              handleOnSelect(SanctionsGroupMarkers.REF, {
                idvStatus: SanctionsGroupMarkers.REF,
              })
            }
          />
          <Chip
            size="small"
            disabled={loading}
            label={`${value[PEPGroupMarkers.REF]} - PEP Referred`}
            sx={{
              backgroundColor: isActive(PEPGroupMarkers.REF)
                ? StatusColor[AmlStatus.REF]
                : DISABLED_COLOR,
            }}
            onDelete={
              isSelected(PEPGroupMarkers.REF)
                ? () => props.onDeselect()
                : undefined
            }
            onClick={() =>
              handleOnSelect(SanctionsGroupMarkers.REF, {
                idvStatus: PEPGroupMarkers.REF,
              })
            }
          />
          <Chip
            size="small"
            disabled={loading}
            label={`${
              value[RiskRatingGroupMarkers.REF]
            } - Risk Rating Referred`}
            sx={{
              backgroundColor: isActive(RiskRatingGroupMarkers.REF)
                ? StatusColor[AmlStatus.REF]
                : DISABLED_COLOR,
            }}
            onDelete={
              isSelected(RiskRatingGroupMarkers.REF)
                ? () => props.onDeselect()
                : undefined
            }
            onClick={() =>
              handleOnSelect(RiskRatingGroupMarkers.REF, {
                riskRatingStatus: RiskRatingGroupMarkers.REF,
              })
            }
          />
        </Stack>
      </Grid>
    </Grid>
  );
};

export default Statistic;
